import { useState, useEffect, useCallback, useRef } from "react";
import { KpiDashboardData } from "../../pages/Dashboard/ChatJess/components/KpiPanel/types";
import { fetchKpiDashboard } from "../../services/kpi/kpiService";
import { mockKpiDashboardData } from "../../mocks/chatJess/mockKpiData";

interface UseKpiDataReturn {
  data: KpiDashboardData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  /** Call this when a kpiDashboardUpdate WebSocket message is received */
  handleWsUpdate: (payload: KpiDashboardData) => void;
}

export const useKpiData = (
  useMock: boolean,
  enabled = true,
): UseKpiDataReturn => {
  const [data, setData] = useState<KpiDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const fetchData = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      setError(null);

      try {
        if (useMock) {
          await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(resolve, 800);
            signal?.addEventListener("abort", () => {
              clearTimeout(timer);
              reject(new DOMException("Aborted", "AbortError"));
            });
          });
          setData({
            ...mockKpiDashboardData,
            lastUpdatedAt: new Date().toISOString(),
          });
        } else {
          const result = await fetchKpiDashboard();
          if (!signal?.aborted && result) {
            setData(result);
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message =
          err instanceof Error ? err.message : "Erro ao carregar KPIs";
        setError(message);
      } finally {
        if (!signal?.aborted) {
          setIsLoading(false);
        }
      }
    },
    [useMock],
  );

  useEffect(() => {
    if (!enabled || hasFetched.current) return;

    hasFetched.current = true;
    const controller = new AbortController();
    fetchData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [enabled, fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleWsUpdate = useCallback((payload: KpiDashboardData) => {
    setData(payload);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, isLoading, error, refetch, handleWsUpdate };
};
